import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import AsyncSelect from 'react-select/async';
import CreatableSelect from 'react-select/creatable';

import { toastSuccess } from '@components/Toast';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { fetchColleaguesDetails, findUserBySearch, updateColleaguesDetails } from '@services/commonService';

import { getUserInfo, MY_COLLEAGUES_TAGS_COLOR_CODE } from '@utils/Util';

const MyColleaguesDetails = ({ id, onBackButtonClick }) => {
	const [colleaguesDetails, setColleaguesDetails] = useState({});
	const [isEdit, setIsEdit] = useState(false);
	const dispatch = useDispatch();
	const [responsibility, setResponsibility] = useState('');
	const [brandTagList, setBrandTagList] = useState([]);
	const [supplierTagList, setSupplierTagList] = useState([]);
	const [thirdPartyTagList, setThirdPartyTagList] = useState([]);
	const [teamLeadIdList, setTeamLeadIdList] = useState([]);

	useEffect(() => {
		loadColleaguesDetails();
	}, []);

	const hasEditPermission = () => {
		const userTypes = ['PEOPLE_MANAGEMENT', 'ADMIN'];
		const teamLeadIds = colleaguesDetails?.teamLeadList?.map((item) => item.id) || [];
		const logedInUserId = getUserInfo()?.id;

		return (
			teamLeadIds.includes(parseInt(logedInUserId)) ||
			parseInt(getUserInfo()?.id) === parseInt(id) ||
			userTypes.includes(getUserInfo()?.primaryUserType)
		);
	};

	const isTeamLeadDisable = () => {
		const typeOfManagementLabel = ['CXO', 'COUNTRY_HEAD', 'DIRECTOR'];
		return (
			colleaguesDetails?.isManagement &&
			typeOfManagementLabel.includes(colleaguesDetails?.managementLevel)
		);
	};

	const loadColleaguesDetails = async () => {
		dispatch(openLoader());
		try {
			const data = await fetchColleaguesDetails(id);
			const teamLeadResponse = data?.teamLeadList.map((member) => ({
				value: member.id,
				label: member.name,
				id: member.id,
				text: member.name
			}));
			setColleaguesDetails(data);
			setBrandTagList(data?.stakeHolderCustomerTagList);
			setSupplierTagList(data?.stakeHolderSupplierTagList);
			setTeamLeadIdList(teamLeadResponse);
			setThirdPartyTagList(data?.stakeHolderThirdPartyTagList);
			setResponsibility(data?.responsibility);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const getOptionsFromTagResponse = (tagResponseList, isName = false) => {
		return tagResponseList.map((tag) => {
			return {
				id: tag?.id,
				text: tag?.text,
				value: tag?.text,
				label: isName ? tag?.name : tag?.text
			};
		});
	};

	const onTagChange = (selected, callable) => {
		let tags = selected.map((tag) => {
			if (tag.__isNew__) {
				return {
					text: tag.label
				};
			} else {
				return {
					text: tag.label,
					id: tag.id
				};
			}
		});
		callable(tags);
	};

	const onChangeMultiSelect = (selected) => {
		const selectedList = selected.map((item) => ({ value: item.value, label: item.label }));
		setTeamLeadIdList([...selectedList]);
	};

	const searchUserByName = async (inputValue) => {
		let params = `?page=0&size=100&search=${inputValue}&isNitexUser=true`;
		let response = await findUserBySearch(params);
		return response?.data?.map((item) => ({
			value: item.id,
			label: item.name
		}));
	};

	const promiseOptions = async (inputValue) => {
		return new Promise((resolve) => {
			setTimeout(async () => {
				resolve(await searchUserByName(inputValue));
			}, 1000);
		});
	};

	const pickRandomColor = () => {
		let result = '';
		if (MY_COLLEAGUES_TAGS_COLOR_CODE.length > 0) {
			const randomIndex = Math.floor(Math.random() * MY_COLLEAGUES_TAGS_COLOR_CODE.length);
			result = MY_COLLEAGUES_TAGS_COLOR_CODE[randomIndex];
		}
		return result;
	};

	const onSaveChange = async () => {
		const requestBody = {
			responsibility: responsibility || null,
			userId: id,
			stakeHolderCustomerTagList: brandTagList || null,
			stakeHolderSupplierTagList: supplierTagList || null,
			stakeHolderThirdPartyTagList: thirdPartyTagList || null,
			teamLeadIdList: teamLeadIdList.map((tag) => tag?.value),
			isOnlyTagUpdate: false
		};
		dispatch(openLoader());
		try {
			await updateColleaguesDetails(requestBody);
			dispatch(closeLoader());
			toastSuccess('Information updated!');
			setIsEdit(false);
			loadColleaguesDetails();
		} catch {
			dispatch(closeLoader());
		}
	};

	const renderStackHolderOptions = () => {
		return (
			<>
				<div className="form-group no-shadow-react-select mb-3">
					<label htmlFor="tskills">Brands</label>
					<CreatableSelect
						value={getOptionsFromTagResponse(brandTagList)}
						isMulti
						onChange={(selected) => onTagChange(selected, setBrandTagList)}
					/>
				</div>
				<div className="form-group no-shadow-react-select mb-3">
					<label htmlFor="tskills">Supplier</label>
					<CreatableSelect
						value={getOptionsFromTagResponse(supplierTagList)}
						isMulti
						onChange={(selected) => onTagChange(selected, setSupplierTagList)}
					/>
				</div>
				<div className="form-group no-shadow-react-select mb-3">
					<label htmlFor="tskills">Other Third Party Stake Holders</label>
					<CreatableSelect
						value={getOptionsFromTagResponse(thirdPartyTagList)}
						isMulti
						onChange={(selected) => onTagChange(selected, setThirdPartyTagList)}
					/>
				</div>
			</>
		);
	};
	const renderJobDetailsOptions = () => {
		return (
			<div className="form-group no-shadow-react-select mb-0 same-padding">
				<label htmlFor="tskills">Team lead</label>
				<AsyncSelect
					value={teamLeadIdList}
					onChange={(selected) => onChangeMultiSelect(selected)}
					isMulti
					cacheOptions
					defaultOptions
					loadOptions={promiseOptions}
					isDisabled={isTeamLeadDisable()}
				/>
			</div>
		);
	};
	const renderResponsibilityInput = () => {
		return (
			<div className="form-group same-padding">
				<textarea
					rows={5}
					name="responsibility"
					id="responsibility"
					value={responsibility}
					placeholder="Responsibility"
					onChange={(e) => {
						setResponsibility(e.target.value);
					}}
				/>
			</div>
		);
	};

	return (
		<div className="back-colleague-list">
			<button className="button text" disabled="" onClick={onBackButtonClick}>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
				>
					<path
						d="M15 18L9 12L15 6"
						stroke="#55585F"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
				<span className="ml-1 gray_dark_02">Back</span>
			</button>
			<div id="profile_details_tab" style={{ paddingBottom: '4rem' }}>
				<div className="employee-profile-info bg-white">
					<div className="container">
						<div className="row">
							<div className="col-md-6 d-flex employee-info-column">
								<div className="employee-profile-picture">
									<img
										src={
											colleaguesDetails?.profilePicUrl
												? colleaguesDetails.profilePicUrl
												: `${process.env.PUBLIC_URL}/images/profile_avatar.png`
										}
										alt=""
									/>
								</div>
								<div className="employee-basic-info">
									<div className="name-designation">
										<h3 className="semibold-18 mb-0">{colleaguesDetails?.name}</h3>
										<span className="regular-12 designation">
											{colleaguesDetails?.designation}
										</span>
										<div className="employee-other-info">
											<p className="regular-14 gray_dark_01">
												{colleaguesDetails?.email}
											</p>
											<p className="regular-14 gray_dark_01">
												{colleaguesDetails?.phone}
											</p>
										</div>
										<div className="linked-profile">
											{colleaguesDetails?.linkedInProfileUrl && (
												<p>
													<img
														src={process.env.PUBLIC_URL + '/icons/linkedin.svg'}
													/>
													<a
														href={colleaguesDetails.linkedInProfileUrl}
														target="_blank"
														className="regular-14 ml-1"
														rel="noreferrer"
													>
														{colleaguesDetails?.linkedUserId}
													</a>
												</p>
											)}
										</div>
									</div>
								</div>
							</div>
							<div className="col-md-6 pl-0">
								<div className="special-skills">
									<h3 className="semibold-12">Special Skills</h3>
									<div className="all-skills">
										{colleaguesDetails?.topThreeSkillTagList?.map((tag, idx) => {
											return (
												<span
													key={`top_three_skill_${idx}`}
													className="tag"
													style={{ background: pickRandomColor() }}
												>
													{tag.text}
												</span>
											);
										})}
										{colleaguesDetails?.otherSkillTagList?.map((tag, idx) => {
											return (
												<span
													key={`other_skill_${idx}`}
													className="tag"
													style={{ background: pickRandomColor() }}
												>
													{tag.text}
												</span>
											);
										})}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="employee-stake-holders bg-white">
					<div className="common-header d-flex justify-content-between align-items-center">
						<div className="one-half py-1">
							<h3 className="semibold-14 mb-0">Certificates</h3>
						</div>
					</div>
					<div className="stake-holders-info">
						<div className="stake-holders-row">
							<div className="all-skills">
								{colleaguesDetails?.certificationTagList?.map((tag, idx) => {
									return (
										<span
											className="tag"
											style={{ background: pickRandomColor() }}
											key={`cetrification_${idx}`}
										>
											{tag.text}
										</span>
									);
								})}
							</div>
						</div>
					</div>
				</div>

				<div>
					<div className="employee-stake-holders bg-white">
						<div className="common-header d-flex justify-content-between align-items-center">
							<div className="one-half py-1">
								<h3 className="semibold-14 mb-0">Job Details</h3>
							</div>
						</div>
						{/* Team lead details */}
						{isEdit && hasEditPermission() ? (
							renderJobDetailsOptions()
						) : (
							<div className="stake-holders-info team-lead d-flex flex-wrap align-item-center">
								{colleaguesDetails?.teamLeadList?.map((teamLead) => (
									<div
										key={teamLead?.id}
										className="team-lead-single d-flex justify-content-between align-items-center"
									>
										<img
											className="lead-img"
											src={teamLead?.profilePicDocument?.docUrl}
											alt={teamLead?.name}
										/>
										<p className="regular-14 gray_dark_02">{teamLead?.name}</p>
									</div>
								))}
							</div>
						)}
					</div>
					{/* Stake Holders */}
					<div className="employee-stake-holders bg-white">
						<div className="common-header d-flex justify-content-between align-items-center">
							<div className="one-half py-1">
								<h3 className="semibold-14 mb-0">Stake Holders</h3>
							</div>
						</div>
						<div className="same-padding">
							{isEdit && hasEditPermission() ? (
								renderStackHolderOptions()
							) : (
								<div className="stake-holders-info p-0">
									<div className="stake-holders-row">
										<h3 className="regular-12 gray_dark_02 mb-1">Customer/ Brands</h3>
										<div className="all-skills">
											{colleaguesDetails?.stakeHolderCustomerTagList?.map(
												(tag, idx) => {
													return (
														<span
															key={`stack_holder_custom_tag_${idx}`}
															className="tag"
															style={{ background: pickRandomColor() }}
														>
															{tag.text}
														</span>
													);
												}
											)}
										</div>
									</div>
									<div className="stake-holders-row">
										<h3 className="regular-12 gray_dark_02 mb-1">Supplier/ Vendor</h3>
										<div className="all-skills">
											{colleaguesDetails?.stakeHolderSupplierTagList?.map(
												(tag, idx) => {
													return (
														<span
															key={`stack_holder_supplier_${idx}`}
															className="tag"
															style={{ background: pickRandomColor() }}
														>
															{tag.text}
														</span>
													);
												}
											)}
										</div>
									</div>
									<div className="stake-holders-row">
										<h3 className="regular-12 gray_dark_02 mb-1">
											Other Thired Party Stake Holders
										</h3>
										<div className="all-skills">
											{colleaguesDetails?.stakeHolderThirdPartyTagList?.map(
												(tag, idx) => {
													return (
														<span
															key={`stack_holder_${idx}`}
															className="tag"
															style={{ background: pickRandomColor() }}
														>
															{tag.text}
														</span>
													);
												}
											)}
										</div>
									</div>
								</div>
							)}
						</div>
					</div>
					{/* Responsibility */}
					<div className="employee-stake-holders bg-white mb-4">
						<div className="common-header d-flex justify-content-between align-items-center">
							<div className="one-half py-1">
								<h3 className="semibold-14 mb-0">Responsibility</h3>
							</div>
						</div>
						{isEdit && hasEditPermission() ? (
							renderResponsibilityInput()
						) : (
							<div className="stake-holders-info same-padding">
								<p className="regular-14">{colleaguesDetails?.responsibility} </p>
							</div>
						)}
					</div>
					{/* Actions Buttons  */}
					<div>
						{isEdit && hasEditPermission() ? (
							<div className="colleagues-button" style={{ display: 'flex', gap: '8px' }}>
								<button
									className="button size32 outline regular-14"
									onClick={() => setIsEdit(false)}
								>
									Cancel
								</button>
								<button className="button size32 regular-14" onClick={onSaveChange}>
									Save change
								</button>
							</div>
						) : (
							hasEditPermission() && (
								<div className="text-right">
									<button
										onClick={() => setIsEdit(true)}
										className="button size32 position-static"
									>
										Edit
									</button>
								</div>
							)
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default MyColleaguesDetails;
